import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTherapists, approveTherapist, rejectTherapist, deleteTherapist } from "../../redux/therapistSlice";
import { getUsers, deleteUser, updateUser } from "../../redux/userSlice";
import { useEffect } from "react";
import { Box, Typography, Button, TextField, Card, CardContent, FormControl, InputLabel, MenuItem, Select, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UserTherapistManagement = () => {
  const [localUsers, setLocalUsers] = useState([
    { id: "1", name: "Ahmet Yılmaz", role: "therapist", status: "approved" },
    { id: "2", name: "Ayşe Demir", role: "user", status: "pending" },
    { id: "3", name: "Mehmet Can", role: "therapist", status: "pending" },
  ]);

  const dispatch = useDispatch();
  const { therapists, loading: therapistLoading, error: therapistError } = useSelector((state) => state.therapist);
  const { users, loading: usersLoading, error: usersError } = useSelector((state) => state.users);

  const [selectionModel, setSelectionModel] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [roleFilter, setRoleeeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState(null);

  useEffect(() => {
    dispatch(getTherapists());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    const mappedTherapists = therapists?.map((therapist) => ({
      id: therapist._id || "",
      name: therapist.name ? `${therapist.name} ${therapist.surname || ""}` : "No Name",
      role: "therapist",
      status: therapist.isVerified ? "approved" : "pending",
    })) || [];

    const mappedUsers = users?.map((user) => ({
      id: user._id || "",
      name: user.name ? `${user.name} ${user.surname || ""}` : "No Name",
      role: "user",
      status: user.isVerified ? "approved" : "pending",
    })) || [];

    setLocalUsers([...mappedTherapists, ...mappedUsers]);
  }, [therapists, users]);

  const handleApprove = async (id) => {
    const target = localUsers.find(u => u.id === id);
    console.log("Approving target:", target);

    if (!target) {
      toast.error("User not found in local list.");
      return;
    }

    if (target.role === "user") {
      // Find the full user object from the redux store if needed, or stick to what we have if the API just needs ID
      const user = users.find(u => u._id === id); 
      if (!user) {
         toast.error("User details not found in store.");
         return;
      }
      
      try {
        await dispatch(updateUser({ ...user, isVerified: true })).unwrap();
        toast.success("User approved!");
        setLocalUsers(prev => prev.map(u => u.id === id ? { ...u, status: "approved" } : u));
      } catch (err) {
        console.error("User approval error:", err);
        toast.error(`Approval failed: ${err.message || err}`);
      }
    } else if (target.role === "therapist") {
      try {
        await dispatch(approveTherapist(id)).unwrap();
        toast.success("Therapist approved!");
        // Update local state immediately or wait for refetch
        dispatch(getTherapists());
      } catch (err) {
        console.error("Therapist approval error:", err);
        toast.error(`Therapist approval failed: ${err.message || err}`);
      }
    }
  };

  const handleReject = async (id) => {
    const target = localUsers.find(u => u.id === id);
    if (!target) {
       toast.error("Target not found.");
       return;
    }

    if (target.role === "user") {
      const user = users.find(u => u._id === id);
      try {
        await dispatch(updateUser({ ...user, isVerified: false, status: "rejected" })).unwrap();
        toast.info("User rejected.");
        dispatch(getUsers());
      } catch (err) {
        console.error("User rejection error:", err);
        toast.error(`User rejection failed: ${err.message || err}`);
      }
    } else if (target.role === "therapist") {
      try {
        await dispatch(rejectTherapist(id)).unwrap();
        toast.info("Therapist rejected!");
        dispatch(getTherapists());
      } catch (err) {
        console.error("Therapist rejection error:", err);
        toast.error(`Therapist rejection failed: ${err.message || err}`);
      }
    }
  };

  const handleDelete = (id) => {
    setSelectedIdToDelete(id);
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (selectedIdToDelete) {
      const target = localUsers.find(u => u.id === selectedIdToDelete);
      
      if (target?.role === "user") {
        dispatch(deleteUser(selectedIdToDelete))
          .unwrap()
          .then(() => {
            toast.success("User deleted successfully!");
            dispatch(getUsers());
          })
          .catch((err) => {
             console.error("Delete user error:", err);
             toast.error(`Failed to delete user: ${err.message || err}`);
          });
      } else if (target?.role === "therapist") {
        dispatch(deleteTherapist(selectedIdToDelete))
          .unwrap()
          .then(() => {
            toast.success("Therapist deleted successfully!");
            dispatch(getTherapists());
          })
          .catch((err) => {
            console.error("Delete therapist error:", err);
            toast.error(`Failed to delete therapist: ${err.message || err}`);
          });
      }
      setSelectedIdToDelete(null);
      setConfirmOpen(false);
    }
  };

  const handleBulkDelete = () => {
    setLocalUsers((prev) => prev.filter((u) => !selectionModel.includes(u.id)));
    setSelectionModel([]);
    toast.success("Selected users deleted!");
  };

  const userColumns = [
    { field: "name", headerName: "Name", width: 200 },
    { field: "id", headerName: "ID", width: 250 },
    { field: "role", headerName: "Rolee", width: 150 },
    { field: "status", headerName: "Status", width: 150,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor:
              params.row.status === "approved"
                ? "#e8f5e9"
                : params.row.status === "pending"
                ? "#fff3e0"
                : "#ffebee",
            borderRadius: "8px",
            padding: "4px 8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor:
                params.row.status === "approved"
                  ? "#4caf50"
                  : params.row.status === "pending"
                  ? "#ff9800"
                  : "#f44336",
              color: "#fff",
              "&:hover": {
                backgroundColor:
                  params.row.status === "approved"
                    ? "#388e3c"
                    : params.row.status === "pending"
                    ? "#fb8c00"
                    : "#e53935",
              },
              fontWeight: "bold",
              fontSize: "0.75rem",
              borderRadius: "8px",
              textTransform: "none",
              minWidth: "90px",
            }}
            disabled
          >
            {params.row.status === "approved"
              ? "Approved"
              : params.row.status === "pending"
              ? "Beklemede"
              : "Rejected"}
          </Button>
        </Box>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 300,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {params.row.status === "pending" && (
            <Button variant="contained" color="success" size="small" onClick={() => handleApprove(params.row.id)}>
              Approve
            </Button>
          )}
          {params.row.status === "pending" && (
            <Button variant="contained" color="warning" size="small" onClick={() => handleReject(params.row.id)}>
              Reject
            </Button>
          )}
          <Button variant="contained" color="error" size="small" onClick={() => handleDelete(params.row.id)}>
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  const filteredUsers = localUsers.filter((user) => {
    const name = (user.name || "").toLowerCase();
    const matchesName = name.includes(filterText.toLowerCase());
    const matchesRoleee = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus = statusFilter === "all" || user.status === statusFilter;
    return matchesName && matchesRoleee && matchesStatus;
  });

  return (
    <>
      <Card sx={{ p: 2 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            User & Therapist Management
          </Typography>
          <Box display="flex" gap={2} mb={2} flexDirection={{ xs: "column", md: "row" }}>
            <FormControl fullWidth size="small">
              <InputLabel>Rolee</InputLabel>
              <Select
                value={roleFilter}
                label="Rolee"
                onChange={(e) => setRoleeeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="therapist">Therapist</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Filter by name"
            variant="outlined"
            size="small"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          {(therapistError || usersError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {therapistError || usersError}
            </Alert>
          )}
          {selectionModel.length > 0 && (
            <Button variant="contained" color="error" onClick={handleBulkDelete} sx={{ mb: 2 }}>
              Delete Selected
            </Button>
          )}
          <div
            style={{
              height: 450,
              width: "100%",
              marginTop: 16,
              marginBottom: 16,
              backgroundColor: "#f9f9f9",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              overflowX: "auto"
            }}
          >
            {(therapistLoading || usersLoading) ? (
              <Typography variant="h6" align="center">Loading...</Typography>
            ) : (
              <div style={{ minWidth: 720 }}>
                <DataGrid
                  rows={filteredUsers || []}
                  columns={userColumns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  checkboxSelection
                  selectionModel={selectionModel}
                  onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                  autoHeight
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default UserTherapistManagement;
